import { useLayoutEffect, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const EULER_ORDER = "XYZ";

/**
 * After Xano/JSON round-trip, `sanitizeRealtimeMessagePayload` turns Vector3/Euler into plain arrays.
 * Receivers must accept both THREE-shaped objects and `[x,y,z]` / `[x,y,z,order]`.
 */
function vec3FromProp(p, target) {
  if (!p) return false;
  if (Array.isArray(p)) {
    target.set(p[0] ?? 0, p[1] ?? 0, p[2] ?? 0);
    return true;
  }
  target.set(p.x ?? 0, p.y ?? 0, p.z ?? 0);
  return true;
}

function eulerFromProp(r) {
  if (!r) return null;
  if (Array.isArray(r)) {
    const x = r[0] ?? 0;
    const y = r[1] ?? 0;
    const z = r[2] ?? 0;
    const order = typeof r[3] === "string" ? r[3] : EULER_ORDER;
    return new THREE.Euler(x, y, z, order);
  }
  return new THREE.Euler(
    r._x ?? r.x ?? 0,
    r._y ?? r.y ?? 0,
    r._z ?? r.z ?? 0,
    EULER_ORDER
  );
}

/**
 * Smooths remote presence position/rotation on the receiver (exponential lerp in useFrame; O(1) per frame).
 * @param {number} [positionScale] — multiply raw position (e.g. 0.5 for editor cursor group offset)
 * @param {number} [stiffness] — higher = snappier (typically 14–28)
 */
export function useSmoothPresenceGroup(
  groupRef,
  { position, rotation },
  positionScale = 1,
  stiffness = 22
) {
  const smoothPos = useRef(new THREE.Vector3());
  const smoothQuat = useRef(new THREE.Quaternion());
  const targetPos = useRef(new THREE.Vector3());
  const targetQuat = useRef(new THREE.Quaternion());
  const initPos = useRef(false);
  const initRot = useRef(false);

  useLayoutEffect(() => {
    if (position && vec3FromProp(position, targetPos.current)) {
      targetPos.current.multiplyScalar(positionScale);
      if (!initPos.current) {
        smoothPos.current.copy(targetPos.current);
        initPos.current = true;
      }
    }
    const e = eulerFromProp(rotation);
    if (e) {
      targetQuat.current.setFromEuler(e);
      if (!initRot.current) {
        smoothQuat.current.copy(targetQuat.current);
        initRot.current = true;
      }
    }
  }, [position, rotation, positionScale]);

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    const t = 1 - Math.exp(-stiffness * delta);
    smoothPos.current.lerp(targetPos.current, t);
    smoothQuat.current.slerp(targetQuat.current, t);
    groupRef.current.position.copy(smoothPos.current);
    groupRef.current.quaternion.copy(smoothQuat.current);
  });
}
